import {put, call} from 'redux-saga/effects';
import Category from "../../entities/category";
import * as Actions from "../actions/categoryActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type CategoryAction = {
    categoryId: string;
    category: Category;
    categories: Category[];
};

export function* fetchOneCategory(action: CategoryAction) {
	try {
		const category: Category = yield call(api.findOne, 'category', action.categoryId);

		// Now we can update the user interface
		yield put({type: Actions.CATEGORY_FETCH_ONE_SUCCESS, categoryId: action.categoryId, category});
	}
	catch(err) {
		yield put({type: Actions.CATEGORY_FETCH_ONE_FAILURE, categoryId: action.categoryId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {CategoryAction} action
 */
export function* saveCategory(action: CategoryAction) {
    // console.debug('⮂ CATEGORY_SAVE saga', action.category?.id);
    
	if (action.category === undefined || action.category === null) {
		console.error('saveCategory received a null');
		throw new Error('saveCategory received a null');
	}

	if (!(action.category instanceof Category)) {
		action.category = new Category(action.category);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'category', action.category);
		if (addResult.isErr()) {
			console.error(`⮂ saveCategory: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.CATEGORY_SAVE_FAILURE, category: action.category, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.CATEGORY_SAVE, category: action.category});

		// Delete this entry from the cache (if there is one). Use uncacheByEntityId so we
		// catch entries regardless of which parentId the writer used (zero for grid views,
		// topLevelId for sub-task views, etc.) — the prior uncacheItem call passed entityId
		// as parentId, which never matched what writers store under, so entries leaked.
		yield call(window.storageWorker.uncacheByEntityId, getCurrentFirmId(), getCurrentUserId(), 'category', action.category.id);

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		yield put({type: Actions.CATEGORY_SAVE_FAILURE, category: action.category, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {CategoryAction} action
 * @param {Category[]} action.categories
 */
export function* saveManyCategories(action: CategoryAction) {
    // console.debug('⮂ CATEGORY_SAVE_MANY saga', action.categories.map(category => category.id).join(','));
    
    if (!Array.isArray(action.categories)) {
        console.error('saveManyCategories expected an array, received', action.categories);
        throw new Error('saveManyCategories expected an array');
    }

	try {
		const toSave: Category[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.categories.length; i++) {
			const category = action.categories[i];
			category.persisted = false;
			toSave.push(category);
		}

		const addResult = yield call(idb.add, 'category', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyCategories: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.CATEGORY_SAVE_MANY_FAILURE, categories: action.categories, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.CATEGORY_SAVE_MANY, categories: toSave});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.CATEGORY_SAVE_MANY_FAILURE, categories: action.categories, status: 500, err});

		console.error(`Error saving ${action.categories.length} categories to database`, err);
	}
}

/**
 * @param {CategoryAction} action
 */
export function* removeCategory(action: CategoryAction) {
    const userId = getCurrentUserId();
	if (!userId || action.category === undefined || action.category === null || !(action.category instanceof Category)) {
			console.error('removeCategory expected type Category, received', action.category);
			throw new Error('removeCategory expected type Category');
		}

	try {
		// Mark as deleted
		action.category.status = 'D';
		action.category.addedOn = newHRTimestamp();
		action.category.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'category', action.category);
		if (addResult.isErr()) {
			console.error(`⮂ removeCategory: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.CATEGORY_REMOVE_FAILURE, category: action.category, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.CATEGORY_REMOVE, category: action.category});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.CATEGORY_REMOVE_FAILURE, category: action.category, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this Category.
	*
	* @param {CategoryAction} action
	*/
export function* restoreCategory(action: CategoryAction) {
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreCategory expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.category === undefined || action.category === null) {
        console.error('restoreCategory expected a Category, received null');
        throw new Error('restoreCategory expected a Category, received null');
    }
    else if (!(action.category instanceof Category)) action.category = new Category(action.category); 
    
	try {
		// Mark as restored
		action.category.status = 'U';
		action.category.addedOn = newHRTimestamp();
		action.category.addedBy = userId;
		action.category.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'category', action.category);
		if (addResult.isErr()) {
			console.error(`⮂ restoreCategory: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.CATEGORY_RESTORE_FAILURE, category: action.category, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.CATEGORY_RESTORE, category: action.category});
	}
	catch(err) {
		yield put({type: Actions.CATEGORY_RESTORE_FAILURE, category: action.category, status: 500, err});

		console.error('Error restoring from database', err);
	}
}